import React from 'react'
import { usePermission, usePermissions } from '../hooks/usePermissions'

interface PermissionGuardProps {
  moduleName: string
  permissionType: 'show' | 'insert' | 'update' | 'delete' | 'view_values'
  children: React.ReactNode
  fallback?: React.ReactNode
  loadingComponent?: React.ReactNode
}

export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  moduleName,
  permissionType,
  children,
  fallback = (
    <div className="flex-1 flex items-center justify-center py-12">
      <div className="text-center">
        <div className="text-xl font-semibold text-gray-500 dark:text-gray-400 mb-2">
          Acesso Negado
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Você não tem permissão para acessar esta página.
        </div>
      </div>
    </div>
  ),
  loadingComponent = <div className="p-4 text-center">Carregando permissões...</div>
}) => {
  const { hasPermission, loading } = usePermission(moduleName, permissionType)

  if (loading) {
    return <>{loadingComponent}</>
  }

  if (!hasPermission) {
    return <>{fallback}</>
  }

  return <>{children}</>
}

// Componente para verificar múltiplas permissões
interface MultiPermissionGuardProps {
  permissions: Array<{
    moduleName: string
    permissionType: 'show' | 'insert' | 'update' | 'delete' | 'view_values'
  }>
  children: React.ReactNode
  fallback?: React.ReactNode
  loadingComponent?: React.ReactNode
  requireAll?: boolean // true = todas as permissões, false = pelo menos uma
}

export const MultiPermissionGuard: React.FC<MultiPermissionGuardProps> = ({
  permissions,
  children,
  fallback = (
    <div className="flex-1 flex items-center justify-center py-12">
      <div className="text-center">
        <div className="text-xl font-semibold text-gray-500 dark:text-gray-400 mb-2">
          Acesso Negado
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Você não tem permissão para acessar esta página.
        </div>
      </div>
    </div>
  ),
  loadingComponent = <div className="p-4 text-center">Carregando permissões...</div>,
  requireAll = true
}) => {
  // Para múltiplas permissões, vamos usar uma abordagem diferente
  // Por enquanto, vamos verificar apenas a primeira permissão
  const { hasPermission, loading } = usePermission(permissions[0].moduleName, permissions[0].permissionType)

  if (loading) {
    return <>{loadingComponent}</>
  }

  if (!hasPermission) {
    return <>{fallback}</>
  }

  return <>{children}</>
}

// Componente para verificar se é admin
interface AdminGuardProps {
  children: React.ReactNode
  fallback?: React.ReactNode
  loadingComponent?: React.ReactNode
}

export const AdminGuard: React.FC<AdminGuardProps> = ({
  children,
  fallback = <div className="p-4 text-center text-gray-500">Acesso restrito a administradores.</div>,
  loadingComponent = <div className="p-4 text-center">Verificando permissões...</div>
}) => {
  const { isAdmin, loading } = usePermissions()

  if (loading) {
    return <>{loadingComponent}</>
  }

  if (!isAdmin) {
    return <>{fallback}</>
  }

  return <>{children}</>
}
